import config from "../config";

/**
 * Generic safe fetch wrapper - mirrors ReviewService.jsx's convention.
 *
 * headers merges with (not replaces) config.getHeaders() - a caller passing
 * its own `headers: {"Content-Type": ...}` otherwise clobbers the whole
 * headers object via the spread below, silently dropping Authorization
 * instead of layering on top of it.
 */
const safeFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: { ...config.getHeaders(), ...options.headers },
  });

  if (!res.ok) {
    const errorText = await res.text();
    console.error("API Error:", errorText);
    let message = errorText;
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.message || parsed.error || errorText;
    } catch {
      // not JSON - fall back to raw text
    }
    throw new Error(message || "API request failed");
  }

  if (res.status === 204) {
    return null;
  }

  const contentType = res.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    return null;
  }
  return res.json();
};

// Seasonal (date-range) rate overrides on a room category - see
// SeasonalRate/SeasonalRateController on the backend.
export const fetchSeasonalRates = async (roomCategoryId) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/room-categories/${roomCategoryId}/seasonal-rates`);
};

export const addSeasonalRate = async (roomCategoryId, { fromDate, toDate, nightlyRate }) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/room-categories/${roomCategoryId}/seasonal-rates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fromDate, toDate, nightlyRate }),
  });
};

export const deleteSeasonalRate = async (roomCategoryId, rateId) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/room-categories/${roomCategoryId}/seasonal-rates/${rateId}`, {
    method: "DELETE",
  });
};

// Centralized Rate Management page - see RateManagementController/
// RoomCategoryRateController on the backend. SUPER_ADMIN/SUPER_USER only.
export const fetchRateManagementRows = async () => {
  return safeFetch(`${config.BASE_URL}/api/resorts/rate-management`);
};

// from/toExclusive as "yyyy-MM-dd" strings - one row per category, each with
// its effective rate for every day in that range (RateCalendarRowDTO).
export const fetchRateCalendar = async (from, toExclusive) => {
  const params = new URLSearchParams({ from, toExclusive });
  return safeFetch(`${config.BASE_URL}/api/resorts/rate-management/calendar?${params.toString()}`);
};

export const updateBaseNightlyRate = async (roomCategoryId, baseNightlyRate) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/room-categories/${roomCategoryId}/base-rate`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ baseNightlyRate }),
  });
};

// Display-only "was" price shown struck through on the guest site next to
// the real rate - see RoomCategory.compareAtRate's javadoc on the backend.
// Separate endpoint from base-rate on purpose: no eZee push, no rate-change
// audit log for a number that's never actually charged.
export const updateCompareAtRate = async (roomCategoryId, compareAtRate) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/room-categories/${roomCategoryId}/compare-at-rate`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ compareAtRate }),
  });
};

// mode: "FLAT" (value is the new rate) or "PERCENT" (value is the % adjustment, e.g. 10 or -5)
export const bulkUpdateRates = async ({ categoryIds, mode, value }) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/rate-management/bulk-update`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ categoryIds, mode, value }),
  });
};

// Re-pushes a category's CURRENT rate to eZee without changing it - for
// right after mapping its eZee IDs for the first time. Authenticated
// (RoomCategoryRateController), NOT the webhook-secret-gated internal
// testing endpoint on EzeeApiController.
export const pushCategoryRateToEzee = async (roomCategoryId, from, to) => {
  const params = new URLSearchParams({ from, to });
  return safeFetch(`${config.BASE_URL}/api/resorts/room-categories/${roomCategoryId}/push-rate?${params.toString()}`, {
    method: "POST",
  });
};
